<script setup>
import { useAssessment } from '@/composables/useAssessment'
import { useRouter } from 'vue-router'

definePage({
  meta: {
    layout: 'default',
    action: 'template-manage',
    subject: 'Assessment',
  },
})

const router = useRouter()
const { saving, createTemplate } = useAssessment()

const form = ref({
  assessment_category_id: null,
  indicator_category_id: null,
  name: '',
  description: '',
  is_active: true,
  criteria: [{ label: '', weight: 0, max_score: 100 }],
})

const errors = ref({})

const addCriterion = () => form.value.criteria.push({ label: '', weight: 0, max_score: 100 })
const removeCriterion = index => form.value.criteria.splice(index, 1)

const totalWeight = computed(() => form.value.criteria.reduce((sum, c) => sum + (Number(c.weight) || 0), 0))
const weightIsValid = computed(() => Math.abs(totalWeight.value - 100) < 0.01)

const save = async () => {
  errors.value = {}
  try {
    await createTemplate(form.value)

    router.push({ name: 'assessment-templates' })
  } catch (e) {
    if (e?.status === 422) errors.value = e?.data?.errors ?? {}
    else console.error('Create assessment template error:', e)
  }
}
</script>

<template>
  <div>
    <h4 class="text-h4 font-weight-bold mb-1">
      Buat Template Penilaian
    </h4>
    <p class="text-body-2 text-medium-emphasis mb-4">
      Total bobot seluruh kriteria harus tepat 100%
    </p>

    <VCard max-width="720">
      <VCardText class="pa-6">
        <VRow>
          <VCol cols="12">
            <MasterDataPicker
              v-model="form.assessment_category_id"
              entity="assessment-categories"
              label="Kategori Penilaian *"
              :error-messages="errors.assessment_category_id"
            />
          </VCol>
          <VCol cols="12">
            <MasterDataPicker
              v-model="form.indicator_category_id"
              entity="indicator-categories"
              label="Kategori Indikator (opsional, khusus KPI)"
              :error-messages="errors.indicator_category_id"
            />
          </VCol>
          <VCol cols="12">
            <VTextField
              v-model="form.name"
              label="Nama Template *"
              :error-messages="errors.name"
              autofocus
            />
          </VCol>
          <VCol cols="12">
            <VTextarea
              v-model="form.description"
              label="Deskripsi"
              rows="2"
              :error-messages="errors.description"
            />
          </VCol>
          <VCol cols="12">
            <VSwitch
              v-model="form.is_active"
              label="Aktif"
              color="success"
              hide-details
            />
          </VCol>
        </VRow>

        <VDivider class="my-4" />

        <div class="d-flex align-center justify-space-between mb-3">
          <p class="text-body-1 font-weight-medium mb-0">
            Kriteria & Bobot
          </p>
          <VBtn
            size="small"
            variant="tonal"
            prepend-icon="ri-add-line"
            @click="addCriterion"
          >
            Tambah Kriteria
          </VBtn>
        </div>

        <VRow
          v-for="(criterion, index) in form.criteria"
          :key="index"
          class="align-center"
        >
          <VCol
            cols="12"
            sm="5"
          >
            <VTextField
              v-model="criterion.label"
              :label="`Label Kriteria ${index + 1} *`"
              :error-messages="errors[`criteria.${index}.label`]"
              density="compact"
            />
          </VCol>
          <VCol
            cols="6"
            sm="3"
          >
            <VTextField
              v-model.number="criterion.weight"
              type="number"
              label="Bobot (%)"
              density="compact"
              :error-messages="errors[`criteria.${index}.weight`]"
            />
          </VCol>
          <VCol
            cols="6"
            sm="3"
          >
            <VTextField
              v-model.number="criterion.max_score"
              type="number"
              label="Skor Maks"
              density="compact"
            />
          </VCol>
          <VCol
            cols="12"
            sm="1"
          >
            <VBtn
              icon="ri-delete-bin-line"
              variant="text"
              color="error"
              :disabled="form.criteria.length <= 1"
              @click="removeCriterion(index)"
            />
          </VCol>
        </VRow>

        <VAlert
          :type="weightIsValid ? 'success' : 'warning'"
          variant="tonal"
          density="compact"
          class="mt-2"
        >
          Total bobot saat ini: {{ totalWeight }}% {{ weightIsValid ? '' : '(harus tepat 100%)' }}
        </VAlert>
        <p
          v-if="errors.criteria"
          class="text-error text-caption mt-2"
        >
          {{ errors.criteria[0] }}
        </p>
      </VCardText>
      <VCardActions class="pa-6 pt-0">
        <VSpacer />
        <VBtn
          variant="tonal"
          color="secondary"
          :disabled="saving"
          @click="router.push({ name: 'assessment-templates' })"
        >
          Batal
        </VBtn>
        <VBtn
          color="primary"
          :loading="saving"
          @click="save"
        >
          Simpan
        </VBtn>
      </VCardActions>
    </VCard>
  </div>
</template>
